import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import useApi from "../../CustomHooks/useApi";
import * as XLSX from "xlsx";

const BASE = process.env.REACT_APP_API_BASE_URL;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const ROLES = ["ALL", "MERCHANT", "DISTRIBUTOR", "MASTER_DISTRIBUTOR"];

const ROLE_STYLE = {
  MERCHANT: { badge: "bg-emerald-100 text-emerald-700", pill: "bg-emerald-600 text-white", ring: "ring-emerald-400" },
  DISTRIBUTOR: { badge: "bg-blue-100 text-blue-700", pill: "bg-blue-600 text-white", ring: "ring-blue-400" },
  MASTER_DISTRIBUTOR: { badge: "bg-violet-100 text-violet-700", pill: "bg-violet-600 text-white", ring: "ring-violet-400" },
};

const STATUS_STYLE = {
  PENDING: "bg-amber-100 text-amber-700",
  APPROVED: "bg-emerald-100 text-emerald-700",
  REJECTED: "bg-red-100 text-red-600",
};

const inp = "w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F] focus:bg-white transition";

/* ─── File preview modal ─── */
const PreviewModal = ({ invoice, onClose }) => {
  if (!invoice?.invoiceUrl) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="relative bg-white rounded-3xl overflow-hidden max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="text-sm font-black text-gray-900">
              Invoice — {invoice.invoiceId}
            </p>
            <p className="text-xs text-gray-400">
              {invoice.targetUserId || invoice.shopName}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <a
              href={invoice.invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="px-3 py-2 rounded-lg bg-[#0A2A4F] text-white text-xs font-bold hover:bg-[#0d3562]"
            >
              View New Tab
            </a>

            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="p-4 h-[75vh] flex items-center justify-center bg-gray-100 overflow-hidden">
          <object
            data={invoice.invoiceUrl}
            type="application/pdf"
            className="w-full h-full rounded-xl"
          >
            <img
              src={invoice.invoiceUrl}
              alt="Invoice"
              className="w-full h-full object-contain rounded-xl"
            />
          </object>
        </div>
      </div>
    </div>
  );
};

/* ─── Shared table shell ─── */
const InvoiceTable = ({ headers, rows, loading, emptyText, emptyAction, minWidth = 720 }) => (
  <div className="overflow-x-auto">
    {loading ? (
      <div className="py-16 text-center">
        <div className="w-8 h-8 border-2 border-[#0A2A4F] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-gray-400">Loading invoices…</p>
      </div>
    ) : rows.length === 0 ? (
      <div className="py-16 text-center">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center text-2xl mx-auto mb-3">📄</div>
        <p className="text-sm font-semibold text-gray-500">{emptyText}</p>
        {emptyAction}
      </div>
    ) : (
      <table className="w-full" style={{ minWidth }}>
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            {headers.map((h) => (
              <th key={h} className="px-5 py-3.5 text-left text-xs font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">{rows}</tbody>
      </table>
    )}
  </div>
);

/* ═══════════════════════════════════════════════════════════ */
const UploadInvoice = () => {
  const adminId = useSelector((s) => s.auth.employeeDetails?.userId);
  const token = useSelector((s) => s.auth.employeeDetails?.token);

  const [tab, setTab] = useState("all");

  /* ── All invoices (user-raised) ── */
  const [allInvoices, setAllInvoices] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [allSearch, setAllSearch] = useState("");
  const [actionModal, setActionModal] = useState(null);
  const [actionRemarks, setActionRemarks] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const { callApi: getAll } = useApi(`/admin/invoice/all${statusFilter ? `?status=${statusFilter}` : ""}`, "GET");
  const { callApi: approve } = useApi("/admin/invoice/approve", "POST");
  const { callApi: reject } = useApi("/admin/invoice/reject", "POST");

  const loadAll = async () => {
    try { setAllLoading(true); const r = await getAll(); setAllInvoices(Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : []); }
    catch { setAllInvoices([]); } finally { setAllLoading(false); }
  };
  useEffect(() => { loadAll(); }, [statusFilter]);

  const filteredAll = useMemo(() => {
    if (!allSearch.trim()) return allInvoices;
    const s = allSearch.toLowerCase();
    return allInvoices.filter((i) =>
      i.invoiceId?.toLowerCase().includes(s) || i.shopName?.toLowerCase().includes(s)
    );
  }, [allInvoices, allSearch]);

  const handleAction = async () => {
    if (!actionModal) return;
    try {
      setActionLoading(true);
      const payload = { invoiceId: actionModal.invoice.invoiceId, adminId, remarks: actionRemarks };
      if (actionModal.type === "APPROVE") await approve(payload); else await reject(payload);
      setActionModal(null); loadAll();
    } catch (err) { alert(err?.response?.data?.message || "Action failed"); }
    finally { setActionLoading(false); }
  };

  /* ── Admin invoices ── */
  const [adminInvoices, setAdminInvoices] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSearch, setAdminSearch] = useState("");

  const { callApi: getAdminRaised } = useApi("/admin/invoice/admin-raised", "GET");

  const loadAdmin = async () => {
    try { setAdminLoading(true); const r = await getAdminRaised(); setAdminInvoices(Array.isArray(r) ? r : []); }
    catch { setAdminInvoices([]); } finally { setAdminLoading(false); }
  };

  const filteredAdmin = useMemo(() => {
    if (!adminSearch.trim()) return adminInvoices;
    const s = adminSearch.toLowerCase();
    return adminInvoices.filter((i) =>
      i.invoiceId?.toLowerCase().includes(s) ||
      i.targetUserId?.toLowerCase().includes(s) ||
      i.shopName?.toLowerCase().includes(s)
    );
  }, [adminInvoices, adminSearch]);

  useEffect(() => {
    if (tab === "admin" && adminInvoices.length === 0 && !adminLoading) loadAdmin();
  }, [tab]);

  /* ── Raise invoice ── */
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [selectedUser, setSelectedUser] = useState(null);

  const [amount, setAmount] = useState("");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [raiseRemarks, setRaiseRemarks] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const { callApi: fetchUsers } = useApi("/admin/users/users-hierarchy/new/v1/not-of-type", "GET");

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const r = await fetchUsers();
      setAllUsers(Array.isArray(r) ? r : []);
    } catch { setAllUsers([]); }
    finally { setUsersLoading(false); }
  };

  useEffect(() => {
    if (tab === "raise" && allUsers.length === 0 && !usersLoading) loadUsers();
  }, [tab]);

  const filteredUsers = useMemo(() => {
    let pool =
      roleFilter === "ALL"
        ? allUsers
        : allUsers.filter((u) => u.role === roleFilter);

    if (userSearch.trim()) {
      const s = userSearch.toLowerCase().trim();

      pool = pool.filter((u) =>
        u?.username?.toLowerCase().includes(s) ||
        u?.fullName?.toLowerCase().includes(s) ||
        u?.mobileNumber?.toLowerCase().includes(s) ||
        u?.bpUserId?.toLowerCase().includes(s) ||
        u?.userId?.toLowerCase().includes(s)
      );
    }

    return pool;
  }, [allUsers, userSearch, roleFilter]);

  const resetForm = () => {
    setAmount(""); setRaiseRemarks(""); setFiles([]);;
    setMonth(new Date().getMonth() + 1); setYear(new Date().getFullYear());
    setUploadError("");
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      setUploadError("Please select a user from the list.");
      return;
    }

    if (!files.length) {
      setUploadError("Please attach at least one file.");
      return;
    }

    try {
      setUploading(true);
      setUploadError("");

      const fd = new FormData();
      fd.append("targetUserId", selectedUser.userId);
      fd.append("adminId", adminId);
      fd.append("amount", 0);
      fd.append("month", month);
      fd.append("year", year);
      fd.append("remarks", raiseRemarks);

      files.forEach((file) => {
        fd.append("files", file);
      });

      const { data } = await axios.post(
        `${BASE}/admin/invoice/upload-for-users-bulk`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Check response
      const allSuccess =
        Array.isArray(data) &&
        data.length > 0 &&
        data.every((item) => item?.status === "SUCCESS");

      if (allSuccess) {
        setUploadSuccess(true);
        setSelectedUser(null);
        resetForm();
        loadAdmin();

        setTimeout(() => {
          setUploadSuccess(false);
          setTab("admin");
        }, 2000);
      } else {
        const failedItem = data?.find(
          (item) => item?.status !== "SUCCESS"
        );

        setUploadError(
          failedItem?.message ||
          failedItem?.invoice?.adminRemarks ||
          "Some invoices failed to upload."
        );
      }
    } catch (err) {
      setUploadError(
        err?.response?.data?.message ||
        "Upload failed. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  /* ── Excel exports ── */
  const downloadAllExcel = () => {
    if (!filteredAll.length) return;
    const rows = filteredAll.map((inv) => ({
      "Invoice ID":           inv.invoiceId ?? "-",
      "Shop Name":            inv.shopName ?? "-",
      "BP User ID":           inv.bpUserId ?? "-",
      "Full Name":            inv.fullName ?? "-",
      "GST Number":           inv.gstNumber ?? "-",
      "State":                inv.state ?? "-",
      "Month / Year":         inv.commissionMonthLabel || `${MONTHS[(inv.invoiceMonth ?? 1) - 1]} ${inv.invoiceYear}`,
      "Invoice Amount":       inv.invoiceAmount ?? 0,
      "Commission":           inv.actualCommissionForMonth ?? 0,
      "CGST Amount":          inv.cgstAmount ?? 0,
      "SGST Amount":          inv.sgstAmount ?? 0,
      "IGST Amount":          inv.igstAmount ?? 0,
      "Total After GST":      inv.totalAfterGst ?? 0,
      "TDS Amount":           inv.tdsAmount ?? 0,
      "Final Payable Amount": inv.finalPayableAmount ?? 0,
      "Status":               inv.status ?? "-",
      "Requester Remarks":    inv.requesterRemarks ?? "-",
      "Raised At":            inv.raisedAt ?? "-",
      "Invoice URL":          inv.invoiceUrl ?? "-",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "All Invoices");
    XLSX.writeFile(wb, `All_Invoices_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const downloadAdminExcel = () => {
    if (!filteredAdmin.length) return;
    const rows = filteredAdmin.map((inv) => ({
      "Invoice ID":    inv.invoiceId ?? "-",
      "Target User ID": inv.targetUserId ?? "-",
      "Shop Name":     inv.shopName ?? "-",
      "Month / Year":  `${MONTHS[(inv.invoiceMonth ?? 1) - 1]} ${inv.invoiceYear}`,
      "Amount":        inv.invoiceAmount ?? 0,
      "Status":        inv.status ?? "-",
      "Admin Remarks": inv.adminRemarks ?? "-",
      "Raised At":     inv.raisedAt ?? "-",
      "Invoice URL":   inv.invoiceUrl ?? "-",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Admin Invoices");
    XLSX.writeFile(wb, `Admin_Invoices_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const TABS = [
    { id: "all", label: "All Invoices" },
    { id: "admin", label: "Admin Invoices" },
    { id: "raise", label: "Raise Invoice" },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-5 min-h-full bg-[#EEF1F5]">

      {/* Header */}
      <div className="bg-gradient-to-r from-[#0A2A4F] via-[#0d3562] to-[#1a5298] rounded-3xl p-6 text-white relative overflow-hidden">
        <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full bg-white/5" />
        <div className="absolute right-20 bottom-0 translate-y-1/2 w-24 h-24 rounded-full bg-white/5" />
        <div className="relative">
          <p className="text-xs font-bold text-white/50 uppercase tracking-widest mb-1">Invoice Management</p>
          <h1 className="text-2xl font-black">Invoice Portal</h1>
          <p className="text-sm text-white/50 mt-1">View, approve, reject and raise invoices for retailers</p>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="bg-white/10 text-white text-xs font-bold px-3 py-1.5 rounded-full">
              {allInvoices.length} User Invoice{allInvoices.length !== 1 ? "s" : ""}
            </span>
            <span className="bg-white/10 text-white text-xs font-bold px-3 py-1.5 rounded-full">
              {adminInvoices.length} Admin Invoice{adminInvoices.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 bg-white border border-gray-100 rounded-2xl p-1.5 shadow-sm w-fit">
        {TABS.map(({ id, label }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold transition ${tab === id ? "bg-[#0A2A4F] text-white shadow-sm" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
          >{label}</button>
        ))}
      </div>

      {/* ════ All Invoices ════ */}
      {tab === "all" && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <h2 className="text-base font-black text-gray-900">User-Raised Invoices</h2>
              <p className="text-xs text-gray-400 mt-0.5">Invoices submitted by merchants — approve or reject</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F] transition">
                <option value="">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
              <input type="text" value={allSearch} onChange={(e) => setAllSearch(e.target.value)}
                placeholder="Search…"
                className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F] transition w-40" />
              <button onClick={downloadAllExcel} disabled={!filteredAll.length}
                className="px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-40 transition">
                ↓ Excel ({filteredAll.length})
              </button>
              <button onClick={loadAll} className="px-4 py-2.5 bg-[#0A2A4F] text-white rounded-xl text-sm font-bold hover:bg-[#0d3562] transition">Refresh</button>
            </div>
          </div>
          <InvoiceTable
            loading={allLoading} emptyText="No invoices found" minWidth={1960}
            headers={["Invoice ID", "Shop", "Full Name", "GST No.","Gst Trade Name", "State", "Month / Year", "Inv. Amount", "Commission", "CGST", "SGST", "IGST", "Total + GST", "TDS", "Final Payable", "Status", "Remarks", "Raised At", "File", "Action"]}
            rows={filteredAll.map((inv) => (
              <tr key={inv.invoiceId} className="hover:bg-gray-50/50 transition">
                <td className="px-5 py-4"><span className="text-xs font-mono font-bold text-gray-600 bg-gray-100 px-2 py-1 rounded-lg">{inv.invoiceId}</span></td>
                <td className="px-5 py-4">
                  <p className="text-sm font-semibold text-gray-800 whitespace-nowrap">{inv.shopName || "—"}</p>
                  {inv.bpUserId && <p className="text-xs text-gray-400 mt-0.5">{inv.bpUserId}</p>}
                </td>
                <td className="px-5 py-4 text-sm text-gray-700 whitespace-nowrap">{inv.fullName || "—"}</td>
                <td className="px-5 py-4"><span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded-lg whitespace-nowrap">{inv.gstNumber || "—"}</span></td>
                <td className="px-5 py-4 text-sm text-gray-600 whitespace-nowrap">{inv.gstTradeName || "—"}</td>
                <td className="px-5 py-4 text-sm text-gray-600 whitespace-nowrap">{inv.state || "—"}</td>
                <td className="px-5 py-4 text-sm text-gray-600 whitespace-nowrap">{inv.commissionMonthLabel || `${MONTHS[(inv.invoiceMonth ?? 1) - 1]} ${inv.invoiceYear}`}</td>
                <td className="px-5 py-4 text-right"><span className="text-sm font-bold text-gray-700">₹{Number(inv.invoiceAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></td>
                <td className="px-5 py-4 text-right"><span className="text-sm font-semibold text-blue-700">₹{Number(inv.actualCommissionForMonth ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></td>
                <td className="px-5 py-4 text-right"><span className="text-sm text-gray-600">₹{Number(inv.cgstAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></td>
                <td className="px-5 py-4 text-right"><span className="text-sm text-gray-600">₹{Number(inv.sgstAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></td>
                <td className="px-5 py-4 text-right"><span className="text-sm text-gray-600">₹{Number(inv.igstAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></td>
                <td className="px-5 py-4 text-right"><span className="text-sm text-gray-700">₹{Number(inv.totalAfterGst ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></td>
                <td className="px-5 py-4 text-right"><span className="text-sm text-amber-700">₹{Number(inv.tdsAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></td>
                <td className="px-5 py-4 text-right"><span className="text-sm font-black text-emerald-700">₹{Number(inv.finalPayableAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></td>
                
                <td className="px-5 py-4">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_STYLE[inv.status] || "bg-gray-100 text-gray-500"}`}>{inv.status}</span>
                </td>
                <td className="px-5 py-4 max-w-[160px]"><p className="text-xs text-gray-400 truncate">{inv.requesterRemarks || "—"}</p></td>
                <td className="px-5 py-4 text-xs text-gray-400 whitespace-nowrap">{inv.raisedAt}</td>
                <td className="px-5 py-4">
                  {inv.invoiceUrl ? (
                    <button onClick={() => setPreview(inv)} className="px-3 py-1.5 bg-[#0A2A4F] text-white text-xs font-bold rounded-lg hover:bg-[#0d3562] transition">View</button>
                  ) : <span className="text-xs text-gray-300">—</span>}
                </td>
                <td className="px-5 py-4">
                  {inv.status === "PENDING" && (
                    <div className="flex gap-2">
                      <button onClick={() => { setActionModal({ invoice: inv, type: "APPROVE" }); setActionRemarks(""); }}
                        className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 transition">Approve</button>
                      <button onClick={() => { setActionModal({ invoice: inv, type: "REJECT" }); setActionRemarks(""); }}
                        className="px-3 py-1.5 bg-red-500 text-white text-xs font-bold rounded-lg hover:bg-red-600 transition">Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          />
        </div>
      )}

      {/* ════ Admin Invoices ════ */}
      {tab === "admin" && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <h2 className="text-base font-black text-gray-900">Admin-Raised Invoices</h2>
              <p className="text-xs text-gray-400 mt-0.5">All invoices issued by admin to retailers</p>
            </div>
            <div className="flex items-center gap-2">
              <input type="text" value={adminSearch} onChange={(e) => setAdminSearch(e.target.value)}
                placeholder="Search…"
                className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F] transition w-44" />
              <button onClick={downloadAdminExcel} disabled={!filteredAdmin.length}
                className="px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-40 transition">
                ↓ Excel ({filteredAdmin.length})
              </button>
              <button onClick={loadAdmin} className="px-4 py-2.5 bg-[#0A2A4F] text-white rounded-xl text-sm font-bold hover:bg-[#0d3562] transition">Refresh</button>
            </div>
          </div>
          <InvoiceTable
            loading={adminLoading} emptyText="No admin-raised invoices yet"
            emptyAction={
              <button onClick={() => setTab("raise")}
                className="mt-4 px-5 py-2.5 bg-[#0A2A4F] text-white rounded-xl text-sm font-bold hover:bg-[#0d3562] transition">
                Raise First Invoice
              </button>
            }
            headers={["Invoice ID", "Target User", "Month / Year", "Amount", "Status", "Remarks", "Raised At", "File"]}
            rows={filteredAdmin.map((inv) => (
              <tr key={inv.invoiceId} className="hover:bg-gray-50/50 transition">
                <td className="px-5 py-4"><span className="text-xs font-mono font-bold text-gray-600 bg-gray-100 px-2 py-1 rounded-lg">{inv.invoiceId}</span></td>
                <td className="px-5 py-4">
                  <p className="text-sm font-bold text-gray-800">{inv.targetUserId}</p>
                  {inv.shopName && <p className="text-xs text-gray-400">{inv.shopName}</p>}
                </td>
                <td className="px-5 py-4 text-sm text-gray-600">{MONTHS[(inv.invoiceMonth ?? 1) - 1]} {inv.invoiceYear}</td>
                <td className="px-5 py-4"><span className="text-sm font-black text-emerald-700">₹{inv.invoiceAmount?.toLocaleString()}</span></td>
                <td className="px-5 py-4">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_STYLE[inv.status] || "bg-gray-100 text-gray-500"}`}>{inv.status}</span>
                </td>
                <td className="px-5 py-4 max-w-[160px]"><p className="text-xs text-gray-400 truncate">{inv.adminRemarks || inv.adminRemarks || "—"}</p></td>
                <td className="px-5 py-4 text-xs text-gray-400 whitespace-nowrap">{inv.raisedAt}</td>
                <td className="px-5 py-4">
                  {inv.invoiceUrl ? (
                    <button onClick={() => setPreview(inv)} className="px-3 py-1.5 bg-[#0A2A4F] text-white text-xs font-bold rounded-lg hover:bg-[#0d3562] transition">View</button>
                  ) : <span className="text-xs text-gray-300">—</span>}
                </td>
              </tr>
            ))}
          />
        </div>
      )}

      {/* ════ Raise Invoice ════ */}
      {tab === "raise" && (
        uploadSuccess ? (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center text-4xl mx-auto mb-4">✓</div>
            <h2 className="text-xl font-black text-gray-900 mb-2">Invoice Raised!</h2>
            <p className="text-sm text-gray-400">Switching to Admin Invoices…</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">

            {/* ── LEFT: User search panel ── */}
            <div className="lg:col-span-2 bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-black text-gray-900">Select Retailer</h3>
                <p className="text-xs text-gray-400 mt-0.5">{allUsers.length} users · choose one to raise invoice</p>
              </div>

              {/* Search */}
              <div className="px-4 pt-4 pb-3 space-y-3">
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search name, mobile, username, BP User ID..."
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F] focus:bg-white transition"
                />
                {/* Role pills */}
                <div className="flex flex-wrap gap-1.5">
                  {ROLES.map((r) => {
                    const active = roleFilter === r;
                    const style = ROLE_STYLE[r];
                    return (
                      <button key={r} onClick={() => setRoleFilter(r)}
                        className={`px-3 py-1 rounded-full text-xs font-bold transition ${active
                          ? r === "ALL" ? "bg-gray-800 text-white" : style?.pill
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                          }`}>
                        {r === "ALL" ? "All" : r.replace(/_/g, " ")}
                        {r !== "ALL" && (
                          <span className="ml-1 opacity-70">({allUsers.filter((u) => u.role === r).length})</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* User list */}
              <div className="flex-1 overflow-y-auto max-h-[520px] px-4 pb-4 space-y-2 scrollbar-hide">
                {usersLoading ? (
                  <div className="py-10 text-center">
                    <div className="w-6 h-6 border-2 border-[#0A2A4F] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-xs text-gray-400">Loading users…</p>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-8">No users found</p>
                ) : filteredUsers.map((u) => {
                  const isSelected = selectedUser?.username === u.username;
                  const rs = ROLE_STYLE[u.role];
                  return (
                    <button
                      key={u.username}
                      type="button"
                      onClick={() => { setSelectedUser(u); resetForm(); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2 text-left transition ${isSelected
                        ? "border-[#0A2A4F] bg-[#0A2A4F]/5"
                        : "border-gray-100 hover:border-gray-200 hover:bg-gray-50"
                        }`}
                    >
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black flex-shrink-0 ${isSelected ? "bg-[#0A2A4F] text-white" : "bg-gray-100 text-gray-500"
                        }`}>
                        {(u.fullName || u.username)?.[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold truncate ${isSelected ? "text-[#0A2A4F]" : "text-gray-800"}`}>
                          {u.fullName || u.username}
                        </p>
                        <p className="text-xs text-gray-400">{u.mobileNumber} · {u.username}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold flex-shrink-0 ${rs?.badge || "bg-gray-100 text-gray-500"}`}>
                        {u.role?.replace(/_/g, " ")}
                      </span>
                      {isSelected && (
                        <span className="w-5 h-5 rounded-full bg-[#0A2A4F] flex items-center justify-center text-white text-xs flex-shrink-0">✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── RIGHT: Invoice form ── */}
            <div className="lg:col-span-3">
              {!selectedUser ? (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-16 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl mx-auto mb-4">👈</div>
                  <p className="text-base font-bold text-gray-500">Select a user from the list</p>
                  <p className="text-xs text-gray-400 mt-1">The invoice form will appear here</p>
                </div>
              ) : (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                  {/* Selected user banner */}
                  <div className="bg-gradient-to-r from-[#0A2A4F] to-[#0d3562] px-6 py-4 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center text-white text-base font-black flex-shrink-0">
                      {(selectedUser.fullName || selectedUser.username)?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black text-white truncate">{selectedUser.fullName || selectedUser.username}</p>
                      <p className="text-xs text-white/50">{selectedUser.mobileNumber} · {selectedUser.username}</p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold flex-shrink-0 ${ROLE_STYLE[selectedUser.role]?.badge || "bg-gray-100 text-gray-500"}`}>
                      {selectedUser.role?.replace(/_/g, " ")}
                    </span>
                    <button type="button" onClick={() => setSelectedUser(null)}
                      className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white text-xs transition">✕</button>
                  </div>

                  <form onSubmit={handleUpload} className="p-6 space-y-5">

                    {/* Amount + Month + Year */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Amount (₹) *</label>
                        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                          placeholder="e.g. 4720" className={inp} required />
                      </div> */}
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Month *</label>
                        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={inp} required>
                          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Year *</label>
                        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={inp} required>
                          {years.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Remarks */}
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Remarks</label>
                      <textarea value={raiseRemarks} onChange={(e) => setRaiseRemarks(e.target.value)}
                        rows={2} placeholder="e.g. Commission invoice for May 2026"
                        className={`${inp} resize-none`} />
                    </div>

                    {/* File upload */}
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Invoice File *</label>
                      <label className="flex items-center gap-4 border-2 border-dashed border-gray-200 rounded-2xl px-6 py-5 cursor-pointer hover:border-[#0A2A4F]/40 hover:bg-gray-50/50 transition group">
                        <div className="w-12 h-12 rounded-2xl bg-gray-100 group-hover:bg-[#0A2A4F]/10 flex items-center justify-center text-xl flex-shrink-0 transition">
                          {files ? "📄" : "⬆"}
                        </div>
                        {files.length > 0 ? (
                          <div>
                            <p className="text-sm font-bold text-gray-800">
                              {files.length} file(s) selected
                            </p>

                            {files.map((file, index) => (
                              <p key={index} className="text-xs text-gray-400 mt-1">
                                {file.name} ({(file.size / 1024).toFixed(1)} KB)
                              </p>
                            ))}
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm font-bold text-gray-600">
                              Click to upload PDF or image
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              Supported: PDF, PNG, JPG, JPEG, XLSX, XLS
                            </p>
                          </div>
                        )}
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls"
                          className="hidden"
                          onChange={(e) => setFiles(Array.from(e.target.files || []))}
                        />
                      </label>
                      {files.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setFiles([])}
                          className="mt-1.5 text-xs text-red-500 hover:text-red-600 font-semibold"
                        >
                          Remove all files
                        </button>
                      )}
                    </div>

                    {uploadError && (
                      <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{uploadError}</div>
                    )}

                    <button type="submit" disabled={uploading}
                      className={`w-full py-4 rounded-2xl text-white font-black text-sm transition ${uploading ? "bg-gray-300 cursor-not-allowed" : "bg-[#0A2A4F] hover:bg-[#0d3562]"
                        }`}>
                      {uploading ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Uploading…
                        </span>
                      ) : `Raise Invoice for ${selectedUser.fullName || selectedUser.username}`}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* Approve/Reject modal */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setActionModal(null)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className={`px-6 py-5 ${actionModal.type === "APPROVE" ? "bg-emerald-600" : "bg-red-500"}`}>
              <h3 className="text-lg font-black text-white">{actionModal.type === "APPROVE" ? "Approve Invoice" : "Reject Invoice"}</h3>
              <p className="text-xs text-white/70 mt-0.5">Invoice: {actionModal.invoice.invoiceId}</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4 text-sm">
                <div className="flex justify-between mb-1.5">
                  <span className="text-gray-400">Shop</span>
                  <span className="font-bold text-gray-800">{actionModal.invoice.shopName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount</span>
                  <span className="font-black text-emerald-700">₹{actionModal.invoice.invoiceAmount?.toLocaleString()}</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Remarks</label>
                <textarea rows={3} value={actionRemarks} onChange={(e) => setActionRemarks(e.target.value)}
                  placeholder="Enter remarks…" className={`${inp} resize-none`} />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setActionModal(null)}
                  className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm hover:bg-gray-200 transition">Cancel</button>
                <button onClick={handleAction} disabled={actionLoading}
                  className={`flex-1 py-3 rounded-xl text-white font-bold text-sm transition ${actionLoading ? "bg-gray-300 cursor-not-allowed"
                    : actionModal.type === "APPROVE" ? "bg-emerald-600 hover:bg-emerald-700"
                      : "bg-red-500 hover:bg-red-600"
                    }`}>
                  {actionLoading ? "Processing…" : actionModal.type}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PreviewModal invoice={preview} onClose={() => setPreview(null)} />
    </div>
  );
};

export default UploadInvoice;
